import { ICampaignLead, isOutboundLinkedInOpener } from '../../../interfaces/models/campaign';

export function normalizeCampaignReplyText(s?: string | null): string {
  return (s ?? '').replace(/\s+/g, ' ').trim();
}

/** True when the message is only emoji / reaction symbols (👍 ❤️ 🙏 …), not a textual reply. */
export function isEmojiOnlyCampaignReply(text: string): boolean {
  const t = normalizeCampaignReplyText(text);
  if (!t) return false;
  // App tsconfig targets es5 — \p{Extended_Pictographic} is not available there.
  const withoutEmoji = t
    .replace(/(?:[\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '')
    .replace(/[\uFE0F\u200D]/g, '')
    .trim();
  return withoutEmoji.length === 0;
}

/**
 * LinkedIn message reactions are often surfaced as inbound rows that echo the
 * original message body (same text as our outbound DM) or as emoji-only events.
 */
export function isInboundMessageReactionLike(
  msg: { text?: string; messageContent?: { text?: string } },
  ourOutboundTexts: ReadonlySet<string>,
): boolean {
  const text = normalizeCampaignReplyText(msg.text ?? msg.messageContent?.text);
  // Empty is not a reaction (align with BE). Extension poll still needs real text
  // to persist a reply body — callers should skip empty separately if needed.
  if (!text) return false;
  if (ourOutboundTexts.has(text)) return true;
  if (isEmojiOnlyCampaignReply(text)) return true;
  return false;
}

export function collectOutboundDmTexts(
  lead: Pick<ICampaignLead, 'messages'>,
  conversationMessages: Array<{ isSent?: boolean; text?: string; messageContent?: { text?: string } }>,
): Set<string> {
  const texts = new Set<string>();
  const add = (raw?: string | null) => {
    const t = normalizeCampaignReplyText(raw);
    if (t) texts.add(t);
  };

  // DMs, InMails and the invite note — an echo of any of them is not the lead replying.
  for (const m of lead.messages ?? []) {
    if (isOutboundLinkedInOpener(m)) add(m.content);
  }

  // Entire thread — catches duplicate-echo / reactions and outbound from other
  // campaigns to the same lead (one shared LinkedIn conversation).
  for (const msg of conversationMessages) {
    if (msg.isSent !== true) continue;
    add(msg.text ?? msg.messageContent?.text);
  }

  return texts;
}

export function conversationMessagesList(msgRaw: unknown): any[] {
  if (Array.isArray(msgRaw)) return msgRaw;
  if (msgRaw && typeof msgRaw === 'object' && Array.isArray((msgRaw as any).messages)) {
    return (msgRaw as any).messages;
  }
  return [];
}
